import { BugOutlined, FireOutlined } from '@ant-design/icons'
import { Space, Tooltip } from 'antd'
import Link from 'next/link'

import { ProjectLink } from '@/components/projects/ProjectLink'
import { InfoTooltip } from '@/components/ui/InfoTooltip'
import urls from '@/config/uiUrls'
import { highlightQueryText } from '@/utils/commons'
import { IntlFunction } from '@/utils/messages'

import {
  Project,
  ProjectFeatures,
  ProjectType,
  ProjectUser,
  User,
  Workspace,
  WorkspaceDescriptor,
} from '@/types/index.d'

export enum WafProjectMigrationStatus {
  MIGRATION_REQUIRED = 'MIGRATION_REQUIRED',
  MIGRATION_IN_PROGRESS = 'MIGRATION_IN_PROGRESS',
  MIGRATION_FINISHED = 'MIGRATION_FINISHED',
}

export function getProjectType(project: Project): ProjectType {
  if (project?.organization) {
    return 'ENVIRONMENT'
  } else {
    return 'GIT'
  }
}

export function isProjectTypeRepository(project: Project): boolean {
  return getProjectType(project) === 'GIT'
}

export function isActiveProject(project: Project): boolean {
  return project?.project_status === 'ACTIVE'
}

export function getFeatureAvailabilityForProject(
  feature: keyof ProjectFeatures,
  project: Project
): { isFeatureBehindPaywall: boolean; isFeatureAvailable: boolean } {
  const isFeatureBehindPaywall = isFeatureBehindPaywallForProject(
    feature,
    project
  )
  return {
    isFeatureAvailable:
      isFeatureBehindPaywall || isFeatureAvailableForProject(feature, project),
    isFeatureBehindPaywall,
  }
}

export function isFeatureAvailableForProject(
  feature: keyof ProjectFeatures,
  project: Project
): boolean {
  const flag = project?.connector_descriptor?.features?.[feature]
  return flag === 'AVAILABLE' || flag === true
}

export function isFeatureBehindPaywallForProject(
  feature: keyof ProjectFeatures,
  project: Project
): boolean {
  const flag = project?.connector_descriptor?.features?.[feature]
  return flag === 'PAYWALL'
}

export function getFeatureAvailabilityForWorkspace(
  feature: keyof WorkspaceDescriptor,
  workspace: Workspace
): { isFeatureBehindPaywall: boolean; isFeatureAvailable: boolean } {
  const isFeatureBehindPaywall = isFeatureBehindPaywallForWorkspace(
    feature,
    workspace
  )
  return {
    isFeatureAvailable:
      isFeatureBehindPaywall ||
      isFeatureAvailableForWorkspace(feature, workspace),
    isFeatureBehindPaywall,
  }
}

export function isFeatureAvailableForWorkspace(
  feature: keyof WorkspaceDescriptor,
  workspace: Workspace
): boolean {
  return workspace?.descriptor?.[feature] === 'AVAILABLE'
}

export function isFeatureUnavailableForWorkspace(
  feature: keyof WorkspaceDescriptor,
  workspace: Workspace
): boolean {
  return workspace?.descriptor?.[feature] === 'UNAVAILABLE'
}

export function isFeatureBehindPaywallForWorkspace(
  feature: keyof WorkspaceDescriptor,
  workspace: Workspace
): boolean {
  return workspace?.descriptor?.[feature] === 'PAYWALL'
}

export function getRendererProjectNameAsLink(
  filter: string
): (title: string, project: Project) => JSX.Element {
  return function (title, project): JSX.Element {
    return (
      <ProjectLink
        project={{
          ...project,
          title: highlightQueryText(title, filter, true) as any,
        }}
      />
    )
  }
}

export function getRenderReportRepositoryNameAsLink(
  filter: string,
  t: IntlFunction
): (
  name: string,
  repository: { id: string; repositoryName: string; children: any[] }
) => JSX.Element {
  return function (name, repository): JSX.Element {
    const link = getRendererProjectNameAsLink(filter)(name, {
      id: repository.id,
      title: name,
    })

    if (repository.repositoryName && !repository.children) {
      return (
        <>
          <InfoTooltip
            iconStyle={{ margin: '0 0.5em 0 0.25em', top: '0.125em' }}
            positioning="left"
            status="warning"
            title={t('ReportsHardening.Table.NoDataWarning')}
          />
          {link}
        </>
      )
    } else {
      return link
    }
  }
}

export function getRendererProjectQuickLinks() {
  return function (id: string, project: Project): JSX.Element {
    const repositoryUrl = urls.repositories.getRepositoryUrl(id)
    return project.project_status !== 'ACTIVE' ? null : project.organization ? (
      <>
        <Link href={`${urls.environments.getEnvironmentUrl(id)}/issues`}>
          <BugOutlined title="Open issues report" />
        </Link>
      </>
    ) : (
      <Space direction="horizontal">
        <Link href={`${repositoryUrl}/hardening`}>
          <FireOutlined title="Open hardening report" />
        </Link>
        <Link href={`${repositoryUrl}/issues`}>
          <BugOutlined title="Open issues report" />
        </Link>
      </Space>
    )
  }
}

export function getComponentId(projectType: ProjectType): string {
  return `${
    projectType === 'ENVIRONMENT' ? 'Environment' : 'Repository'
  }Page.PageHeader`
}

export function isProjectLead(
  team: ProjectUser[],
  project: Project,
  user: User
) {
  return (
    team?.find((teamMember) => teamMember.user.id === user?.id)?.role === 'LEAD'
  )
}

export function isProjectMember(team: ProjectUser[], user: User) {
  return team?.find((teamMember) => teamMember.user.id === user?.id)
}

export function onClickableCell({
  name,
  value,
  onCellMouseEnter,
  onCellMouseLeave,
}: {
  name: string
  value: number
  onCellMouseEnter: () => void
  onCellMouseLeave: () => void
}) {
  return {
    onMouseEnter: typeof value !== 'undefined' ? onCellMouseEnter : null,
    onMouseLeave: typeof value !== 'undefined' ? onCellMouseLeave : null,
    name,
    value,
    ['style']: { padding: 0 },
    className: 'customClickableCell',
    onClick: (e) => {
      e.stopPropagation()
    },
  }
}

export function cellWithToolTipFactory({
  t,
  componentId,
}: {
  t: IntlFunction
  componentId:
    | 'RepositoryPullRequestsPane'
    | 'EnvironmentAssessmentsPane'
    | 'RepositoryRevisionsPane'
    | 'OverviewScanPane'
}) {
  return function (props) {
    const cellTitle = props?.name as 'Issues' | 'Score'
    const isNumber = typeof props.value === 'number'

    return (isNumber || typeof props.value === 'object') &&
      typeof cellTitle !== 'undefined' ? (
      <Tooltip title={t(`${componentId}.Table.${cellTitle}.tooltip`)}>
        <td {...props} />
      </Tooltip>
    ) : (
      <td {...props} />
    )
  }
}

export function ConditionalRenderingNumericValue({
  value,
  children,
}: {
  value: number
  children: React.ReactNode
}): React.ReactNode {
  if (Number.isFinite(value)) {
    return children
  }
  return value
}
